import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, Check, X, BookOpen, Users, Clock, Database, ArrowRight, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import timetableService from '../services/timetableService';
import '../styles/Generation.css';

const Generation = ({ onNext }) => {
  const [generationStatus, setGenerationStatus] = useState('ready'); // ready, processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [generatedLessons, setGeneratedLessons] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [processingStep, setProcessingStep] = useState('');

  // Sample data for demonstration
  const [sampleData] = useState([
    { class: '中一A', subject: '中文科', teacher: '陳大文', periods: 5 }
  ]);

  const [generatedSampleLessons] = useState([
    { id: '1001', class: '中一A', subject: '中文科', teacher: '陳大文' },
    { id: '1002', class: '中一A', subject: '中文科', teacher: '陳大文' },
    { id: '1003', class: '中一A', subject: '中文科', teacher: '陳大文' },
    { id: '1004', class: '中一A', subject: '中文科', teacher: '陳大文' },
    { id: '1005', class: '中一A', subject: '中文科', teacher: '陳大文' }
  ]);

  const handleGenerateLessons = async () => {
    setGenerationStatus('processing');
    setProcessingStep('檢查資料設定...');
    
    try {
      // Step 1: Check data setup
      setProcessingStep('檢查資料設定...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Validate class subject lessons
      setProcessingStep('驗證班別科目資料...');
      const classSubjectLessons = await timetableService.getClassSubjectLessons();
      
      if (!classSubjectLessons || classSubjectLessons.length === 0) {
        throw new Error('No class subject lessons found. Please set up planning data first.');
      }

      // Step 3: Check for missing teachers
      setProcessingStep('檢查教師分配...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate finding missing teacher assignment
      const missingTeachers = classSubjectLessons.filter(lesson => !lesson.teacher_name);
      if (missingTeachers.length > 0) {
        setErrorDetails(missingTeachers.map(lesson => ({
          type: 'No Teacher Assigned',
          location: `Page 1--${lesson.class_name}--${lesson.subject_name}`,
          details: `Class: ${lesson.class_name}, Subject: ${lesson.subject_name}`
        })));
        setErrorMessage('Problems generating lessons');
        setGenerationStatus('error');
        setShowErrorDialog(true);
        return;
      }

      // Step 4: Generate lessons
      setProcessingStep('產生課節資料...');
      const result = await timetableService.generateLessons(classSubjectLessons);
      
      if (result.success) {
        setProcessingStep('課節產生完成');
        setGeneratedLessons(result.lessons || []);
        setGenerationStatus('success');
      } else {
        throw new Error(result.message || 'Failed to generate lessons');
      }
    } catch (error) {
      console.error('Error generating lessons:', error);
      setGenerationStatus('error');
      setErrorMessage(error.message || 'An error occurred while generating lessons');
      setErrorDetails([{
        type: 'Generation Error',
        location: 'System',
        details: error.message
      }]);
      setShowErrorDialog(true);
    }
  };

  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false);
    setGenerationStatus('ready');
  };

  const handleRetryGeneration = () => {
    setShowErrorDialog(false);
    handleGenerateLessons();
  };

  const handleGoBackToFix = () => {
    setShowErrorDialog(false);
    setGenerationStatus('ready');
    // Navigate back to planning data or settings
    if (onNext) {
      onNext('planning');
    }
  };

  const renderReadyState = () => (
    <div className="generation-container">
      {/* Header */}
      <div className="generation-header">
        <div className="generation-header-content">
          <div className="generation-icon">
            <RefreshCw className="icon" />
          </div>
          <div className="generation-title-section">
            <h1 className="generation-title">計劃資料 (4): 產生課節 (Lesson Generation)</h1>
            <p className="generation-subtitle">自動產生時間表課節資料</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="generation-content">
        {/* Process Description */}
        <div className="process-description">
          <div className="process-icon">
            <BookOpen className="icon" />
          </div>
          <div className="process-content">
            <h3 className="process-title">課節產生說明</h3>
            <p className="process-text">
              設定受所有的資料後，本功能會讀取各班各科的資料，產生細項的課節資料。
            </p>
          </div>
        </div>

        {/* Example Section */}
        <div className="example-section">
          <h4 className="example-title">範例：</h4>
          
          {/* Input Example */}
          <div className="example-input">
            <div className="example-label">輸入資料：</div>
            <div className="example-data">
              {sampleData.map((item, index) => (
                <div key={index} className="example-item">
                  <span className="class">{item.class}</span>
                  <span className="subject">{item.subject}</span>
                  <span className="teacher">{item.teacher}</span>
                  <span className="periods">{item.periods}節</span>
                </div>
              ))}
            </div>
          </div>

          {/* Arrow */}
          <div className="example-arrow">
            <ArrowRight className="arrow-icon" />
          </div>

          {/* Output Example */}
          <div className="example-output">
            <div className="example-label">產生課節：</div>
            <div className="lessons-list">
              {generatedSampleLessons.map((lesson, index) => (
                <div key={lesson.id} className="lesson-item">
                  <div className="lesson-bullet"></div>
                  <span className="lesson-class">{lesson.class}</span>
                  <span className="lesson-subject">{lesson.subject}</span>
                  <span className="lesson-teacher">{lesson.teacher}</span>
                  <span className="lesson-id">{lesson.id}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Steps Information */}
        <div className="steps-info">
          <h4 className="steps-title">操作步驟：</h4>
          <div className="steps-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                進入策劃模組後，選計劃 (Plan)，再選產生課節 (Lesson Generation)，系統會檢查資料是否設定正確。
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                如系統發現資料錯誤，會顯示錯誤畫面。
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                回到有關的功能修正資料後，再重新執行本功能。
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-content">
                課節產生過程完成。
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="generation-actions">
        <button
          onClick={() => console.log('Close')}
          className="action-btn close-btn"
        >
          <X size={16} />
          關閉 (Close)
        </button>
        
        <button
          onClick={handleGenerateLessons}
          className="action-btn generate-btn"
        >
          <RefreshCw size={16} />
          開始產生課節 (Start Lesson Generation)
        </button>
      </div>
    </div>
  );

  const renderProcessingState = () => (
    <div className="generation-container">
      <div className="processing-state">
        <div className="processing-icon">
          <RefreshCw className="icon spinning" />
        </div>
        <h3 className="processing-title">正在產生課節...</h3>
        <p className="processing-subtitle">{processingStep}</p>
        <div className="processing-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSuccessState = () => (
    <div className="generation-container">
      <div className="success-state">
        <div className="success-icon">
          <CheckCircle className="icon" />
        </div>
        <h3 className="success-title">課節產生完成</h3>
        <p className="success-subtitle">所有課節已成功產生並儲存到資料庫。</p>
        
        <div className="success-details">
          <h4 className="success-details-title">產生結果：</h4>
          <div className="success-list">
            <div className="success-item">
              <CheckCircle size={16} className="success-item-icon" />
              課節已成功產生並儲存
            </div>
            <div className="success-item">
              <CheckCircle size={16} className="success-item-icon" />
              時間表已更新
            </div>
            <div className="success-item">
              <CheckCircle size={16} className="success-item-icon" />
              教師時間表已建立
            </div>
            <div className="success-item">
              <CheckCircle size={16} className="success-item-icon" />
              可以開始查看時間表
            </div>
          </div>
        </div>

        <div className="success-actions">
          <button
            onClick={() => setGenerationStatus('ready')}
            className="action-btn clear-btn"
          >
            <X size={16} />
            清除課節 (Clear Lessons)
          </button>
          
          <button
            onClick={() => onNext && onNext('dataset')}
            className="action-btn next-btn"
          >
            <ArrowRight size={16} />
            下一步：建立資料集 (Next: Data Set)
          </button>
        </div>
      </div>
    </div>
  );

  const renderErrorDialog = () => {
    if (!showErrorDialog) return null;

    return (
      <div className="error-dialog-overlay">
        <div className="error-dialog">
          <div className="error-dialog-header">
            <div className="error-dialog-icon">
              <AlertTriangle className="icon" />
            </div>
            <h3 className="error-dialog-title">Lesson Generation</h3>
          </div>
          
          <div className="error-dialog-content">
            <div className="error-main-message">
              <strong>Problems generating lessons</strong>
            </div>
            
            <div className="error-details">
              {errorDetails.map((error, index) => (
                <div key={index} className="error-detail-item">
                  <div className="error-detail-type">{error.type}:</div>
                  <div className="error-detail-location">{error.location}</div>
                  {error.details && (
                    <div className="error-detail-info">{error.details}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="error-dialog-actions">
            <button
              onClick={handleGoBackToFix}
              className="action-btn fix-btn"
            >
              <ArrowRight size={16} />
              回到有關功能修正資料
            </button>
            
            <button
              onClick={handleRetryGeneration}
              className="action-btn retry-btn"
            >
              <RefreshCw size={16} />
              重新嘗試
            </button>
            
            <button
              onClick={handleCloseErrorDialog}
              className="action-btn ok-btn"
            >
              <Check size={16} />
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {generationStatus === 'ready' && renderReadyState()}
      {generationStatus === 'processing' && renderProcessingState()}
      {generationStatus === 'success' && renderSuccessState()}
      {renderErrorDialog()}
    </>
  );
};

export default Generation;